import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Text, Divider, Grid, Flex, useBoolean } from '@chakra-ui/react';
import {
  TextInput,
  Unform,
  Button,
  SelectInput,
  MaskInput,
  Clients,
  AutocompleteInput,
  Loading,
  Label,
  StatusTag,
  BooleanInput,
  TextAreaInput,
  Modal,
} from 'components';
import { IDemandServiceType, IDemands } from 'types/demands';
import { demandsValidationSchema } from 'yupSchemas/demands';
import { RouteComponentProps } from 'react-router';
import { createDemand } from 'services/createDemand';
import { openNotification } from 'utils/notification';
import { formatDateToBr, formatDateToUs } from 'utils/date';
import { attendanceDeadlineOptions, internalAreaOptions } from 'constants/demand';
import { routePaths } from 'constants/routes';
import { FormHandles } from '@unform/core';
import { changeStatusDemand } from 'services/changeStatusDemand';
import { getDemandData } from 'services/getDemandData';
import { editDemand } from 'services/editDemand';
import { demandStatusLabels, demandStatusValues, demandStatusVariant } from 'constants/demands';
import { format } from 'date-fns';
import { formatCurrency } from 'utils/number';
import { getIDemandServiceTypes } from 'services/demands';
import { getLawyers, Lawyer } from 'services/lawyers';
import useEventBus from 'hooks/useEventBus';
import { checkPermission } from 'utils/authentication';
import { useHistory } from 'react-router-dom';
import { colors } from 'theme/colors';
import { postUserSolicitation } from 'services/userSolicitation';
import { getDemandsData } from 'services/demandsTable';
import { RiTimer2Line as Time } from 'react-icons/ri';
import { getLoggedUser } from 'utils/storage';

interface IUserType {
  admin: boolean;
  id: number;
  name: string;
}

const NewDemandComp: React.FC<RouteComponentProps<{ id?: string }>> = ({ match }) => {
  const history = useHistory();
  const [disable, setDisable] = useBoolean();
  const [hide, setHide] = useBoolean();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isCancel, setIsCancel] = useBoolean();
  const demandID = useMemo(() => match.params.id || false, [match.params.id]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [demand, setDemand] = useState<IDemands>();
  const formRef = useRef<FormHandles>(null);
  const [isSla, setIsSla] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<IDemandServiceType[]>([]);
  const statusRef = useRef('');
  const [lawyerOptions, setLawyerOptions] = useState<Lawyer[]>([]);
  const eventBus = useEventBus();
  const [btnStatus, setBtnStatus] = useState<TBtnStat>('Cancelar demanda?');
  type TBtnStat = 'Cancelar demanda?' | 'Cancelamento Solicitado' | 'Cancelado';

  useEffect(() => {
    eventBus.dispatchEvent(
      'onChangeTitle',
      demandID ? 'Detalhes da Solicitação' : 'Nova Solicitação',
    );
  }, [demandID]);

  const handleGetData = useCallback(async () => {
    if (!demandID) return;
    try {
      setIsLoading(true);
      const response = await getDemandData(demandID);
      setDemand({
        ...response.data,
        prazo: formatDateToBr(response.data.prazo.replace(/T\S+/, '')),
        areaInterna:
          internalAreaOptions.find(i => i.id === response.data.areaInterna) ||
          internalAreaOptions[0],
        horas: response.data.horas.toString().slice(0, 5),
      });
      statusRef.current = response.data.status;
      if (response.data.prazoAtendimento === 'SLA') {
        setIsSla(true);
      } else {
        setIsSla(false);
      }
      if (response.data.status !== demandStatusValues.pending) setHide.on();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [demandID]);

  const handleConfirm = useCallback(async () => {
    setIsCancel.on();
    try {
      if (checkPermission({ onlyAdmin: true })) {
        changeStatusDemand(+demandID, demandStatusValues.canceled)
          .then(() => setDisable.on())
          .then(() => setBtnStatus('Cancelado'))
          .then(() => handleGetData());
      } else {
        changeStatusDemand(+demandID, demandStatusValues.toCancel)
          .then(() => setDisable.on())
          .then(() => setBtnStatus('Cancelamento Solicitado'))
          .then(() => handleGetData());
      }
    } catch (e) {}
  }, [demandID]);

  const getUserType = () => {
    const userType = localStorage.getItem('@dpohub/user');

    if (typeof userType === 'string') {
      const userTp: IUserType = JSON.parse(userType);

      return {
        admin: userTp.admin,
        id: userTp.id,
        name: userTp.name,
      };
    }
  };

  const typeClientAdmin = {
    name: getLoggedUser().clientName,
    id: getLoggedUser().client,
  };

  const typeUserLogged = typeClientAdmin.name ? 'CLIENTE_ADMINISTRADOR' : 'DANIEL_LAW';

  const handleSubmit = useCallback(
    async (formData: IDemands) => {
      try {
        setIsSaving(true);
        const createData = {
          ...formData,

          prazo: formData.areaInterna.id === 'SLA' ? undefined : formatDateToUs(formData?.prazo),
          areaInterna: formData.areaInterna.id,
          status: statusRef.current || undefined,
          horas: `${formData.horas}:00`,
        };
        if (createData.prazo) {
          if (Number(createData.prazo.slice(5, 7)) > 12) {
            openNotification('Data do prazo inválida.', 'warn');
            return;
          } else if (Number(createData.prazo?.slice(-2)) > 31) {
            openNotification('Data do prazo inválida.', 'warn');
            return;
          }
        }
        if (!createData.prazo) delete createData.prazo;
        if (!createData.status || !demandID) delete createData.status;
        if (createData.horas.length !== 8) {
          openNotification('Valor de horas inválido.', 'warn');
          return;
        }

        if (
          Number(createData.horas[0]) === 0 &&
          Number(createData.horas[1]) === 0 &&
          Number(createData.horas[3]) === 0 &&
          Number(createData.horas[4]) === 0
        ) {
          openNotification('Valor de horas inválido.', 'warn');
          return;
        }

        const query = '?page=0';
        const defaultQuery = 'size=10&sort=prazo,asc';
        let newQuery = `?${defaultQuery}`;

        if (query) {
          if (query[0] === '?') {
            newQuery = `${query}&${defaultQuery}`;
          } else {
            newQuery = `?${query}&${defaultQuery}`;
          }
        }

        if (!demandID) {
          // @ts-ignore
          await createDemand(createData);
          const user = getUserType();
          const response = await getDemandsData(newQuery);
          await postUserSolicitation({
            id: 1,
            admin: typeof user?.admin === 'boolean' && user.admin,
            name: typeof user?.name === 'string' && user.name,
            userId: typeof user?.id === 'number' && user.id,
            solicitacaoId: response.data.content.length,
          });
        } else {
          // @ts-ignore
          await editDemand(demandID, createData);
          openNotification('Demanda editada com sucesso!');
          history.push(routePaths.demands);
          return;
        }

        history.push(routePaths.demands);

        openNotification('Demanda criada com sucesso!');
      } catch (error) {
      } finally {
        setIsSaving(false);
      }
    },
    [demandID],
  );

  const handleGetServiceTypes = useCallback(async () => {
    try {
      const response = await getIDemandServiceTypes();

      setServiceTypes(response.data);
    } catch (error) {}
  }, []);

  useEffect(() => {
    handleGetServiceTypes();
    (async () => {
      const { data } = await getLawyers();
      setLawyerOptions(data);
    })();
  }, []);

  useEffect(() => {
    handleGetData();
    getUserType();
  }, []);

  const initialFormData = useMemo(
    () => ({
      client: demand?.client || typeClientAdmin,
      demanda: demand?.demanda,
      serviceType: demand?.serviceType,
      areaInterna: demand?.areaInterna,
      areaDemandante: demand?.areaDemandante,
      autorDemanda: demand?.autorDemanda,
      prazoAtendimento: demand?.prazoAtendimento,
      prazo: demand?.prazo,
      descricaoTrabalho: demand?.descricaoTrabalho,
      lawyer: demand?.lawyer,
      horas: demand?.horas,
      valor: demand?.valor && formatCurrency(demand?.valor as number),
      consumoHoras: !!demand?.consumoHoras,
    }),
    [demand],
  );

  const handleKeyUp = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let value = e.currentTarget.value;
    value = value.replace(/\D/g, '');

    value = value.replace(/(\d)(\d{2})$/, '$1,$2');
    value = value.replace(/(?=(\d{3})+(\D))\B/g, '.');
    e.currentTarget.value = value;
  }, []);

  interface IProps {
    header: TBtnStat;
    onClick(): void;
    disabled: boolean;
    hide: boolean;
  }
  const ModalShow = useCallback(
    (props: IProps) => {
      if (!props.hide) {
        return (
          <Modal
            title={props.header}
            header={props.header}
            body="Tem certeza que deseja cancelar esta demanda? O time Daniel não dará continuidade a esta solicitação."
            yesText="Sim, quero cancelar"
            noText="Não"
            onClick={handleConfirm}
            disabled={props.disabled}
          />
        );
      }
    },
    [hide],
  );

  return (
    <Box w="100%">
      <Box m="0 auto" maxW="50rem" width="100%">
        {demandID && isLoading ? (
          <Loading />
        ) : (
          <Box
            boxShadow="0px 0px 15px rgba(0, 0, 0, 0.25)"
            border="1px solid"
            borderColor="brand.gray100"
            px="22px"
            py="24px"
            background="#fff"
            borderRadius="8px"
            maxWidth="848px"
          >
            <Unform
              onSubmit={handleSubmit}
              validationSchema={demandsValidationSchema}
              formRef={formRef}
              initialData={initialFormData}
            >
              <Grid templateColumns="repeat(1, 1fr)" gridGap="1rem">
                <Clients
                  disableNewClient={!!typeClientAdmin.id || !!demandID}
                  disabled={!!typeClientAdmin.id || (!isEditing && !!demandID)}
                  // initialClient={typeClientAdmin}
                  // formRef={formRef}
                  // placeholder="Digite o tipo de serviço"
                />
              </Grid>
              <Grid templateColumns="repeat(2, 1fr)" gridGap="1rem">
                <Box flex={1}>
                  <AutocompleteInput
                    returnAllOption
                    label="Tipo de Serviço/Modalidade"
                    placeholder="Selecione o tipo de serviço"
                    optionLabel="name"
                    optionValue="id"
                    name="serviceType"
                    options={serviceTypes}
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
              </Grid>
              <Grid templateColumns="1.5fr 1fr" gridGap="1rem">
                <Box flex={1}>
                  <TextInput
                    name="demanda"
                    placeholder="Digite aqui..."
                    label="Título da Demanda"
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
                <Box flex={1}>
                  <AutocompleteInput
                    returnAllOption
                    label="Área Interna"
                    placeholder="Área Interna"
                    optionLabel="name"
                    optionValue="id"
                    name="areaInterna"
                    options={internalAreaOptions}
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
              </Grid>
              <Grid templateColumns="1fr 1.5fr" gridGap="1rem">
                <Box flex={1}>
                  <TextInput
                    name="areaDemandante"
                    placeholder="Selecione"
                    label="Área demandante"
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
                <Box flex={1}>
                  <TextInput
                    name="autorDemanda"
                    placeholder="Digite ou Selecione"
                    label="Autor da Demanda"
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
              </Grid>
              <Grid templateColumns="repeat(3, 1fr)" gridGap="1rem">
                <Box flex={1}>
                  <SelectInput
                    label="Prazo de atendimento"
                    name="prazoAtendimento"
                    optionLabel="name"
                    optionValue="id"
                    options={attendanceDeadlineOptions}
                    isReadOnly={!!demandID}
                    disabled={!isEditing && !!demandID}
                    onChange={() => setIsSla(!isSla)}
                  />
                </Box>
                <Box
                  display={
                    isSla === false && !!demand?.prazo === false
                      ? 'none !important'
                      : 'block !important'
                  }
                  flex={1}
                >
                  <MaskInput
                    mask="date"
                    name="prazo"
                    placeholder="Prazo"
                    label="Prazo"
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
              </Grid>
              <Grid templateColumns="repeat(1, 1fr)" gridGap="1rem">
                <Box flex={1}>
                  <TextAreaInput
                    name="descricaoTrabalho"
                    placeholder="Digite aqui..."
                    label="Descrição do trabalho"
                    rows={4}
                    isDisabled={!isEditing && !!demandID}
                  />
                </Box>
              </Grid>
              <Grid templateColumns="repeat(3, 1fr)" gridGap="1rem">
                <Box flex={1}>
                  <AutocompleteInput
                    returnAllOption
                    label="Advogado(a)"
                    placeholder="Selecione..."
                    name="lawyer"
                    optionLabel="name"
                    optionValue="id"
                    options={lawyerOptions}
                    maxHeightSuggestion={12}
                    disabled={!isEditing && !!demandID}
                  />
                </Box>
                <Box flex={1} display="flex" position="relative">
                  <TextInput
                    name="horas"
                    type="text"
                    label="Horas"
                    placeholder="00:00"
                    border={`1px solid ${colors.brand.gray500}`}
                    // maxLength={9}
                    disabled={!isEditing && !!demandID}
                    onKeyPress={(ev: any) => {
                      const timer = ev.target;

                      if (timer.value.length === 5) {
                        timer.value = timer.value.slice(0, 4);
                      }
                      if (timer.value.length === 2) {
                        timer.value += ':';
                      }

                      if (Number(timer.value[3]) > 5) {
                        timer.value = timer.value.slice(0, 3);
                      }
                    }}
                  />
                  <Time
                    style={{ position: 'absolute', right: '1rem', top: '1.9rem', zIndex: 1000 }}
                    fontSize={24}
                  />
                </Box>
                <Box flex={1} position="relative">
                  <Text position="absolute" left="10px" top="1.9rem" zIndex={1000}>
                    R$
                  </Text>
                  <MaskInput
                    mask={'newCurrency'}
                    name="valor"
                    placeholder="00,00"
                    label="Valor"
                    parseToFloat
                    disabled={!isEditing && !!demandID}
                    onKeyUp={handleKeyUp}
                    paddingLeft="1.9rem"
                    _placeholder={{ paddingLeft: '0rem' }}
                  />
                </Box>
              </Grid>
              <Flex>
                <Box>
                  <Text mb="0.5rem" as="h5" fontSize="14px;" fontWeight="bold">
                    Solicitação válida para consumo de horas?
                  </Text>
                  <BooleanInput mb="1rem" name="consumoHoras" disabled={!isEditing && !!demandID} />
                </Box>

                {demandID && !isEditing && (
                  <Flex marginLeft="auto" gridGap="3rem">
                    <Flex direction="column">
                      <Label>Data criação</Label>
                      <p>{formatDateToBr(demand?.createdAt || '')}</p>
                    </Flex>

                    <Flex direction="column">
                      <Label>Status</Label>
                      <StatusTag
                        variant={demand?.status ? demandStatusVariant[demand?.status] : 'accept'}
                        demandArchived={demand?.archived}
                      >
                        {demand?.archived === true
                          ? 'Arquivada'
                          : demandStatusLabels[demand?.status]}
                      </StatusTag>
                    </Flex>

                    <Flex direction="column">
                      <Label>Data alteração</Label>
                      <p>
                        {demand?.updatedAt &&
                          format(new Date(demand?.updatedAt || ''), 'dd/MM/yyyy')}
                      </p>
                    </Flex>
                  </Flex>
                )}
              </Flex>
              <Divider m="0.4rem 0" mb="24px" />
              {demandID && !isEditing ? (
                typeUserLogged === 'DANIEL_LAW' ? (
                  <Flex justifyContent="space-between">
                    <Box>
                      <Button
                        leftIcon={{ name: 'pencil', marginRight: 'inherit' }}
                        onClick={() => setIsEditing(true)}
                        type="button"
                        variant="primary"
                        outline
                      >
                        Editar Demanda
                      </Button>
                    </Box>
                    <Box>
                      {ModalShow({
                        header: btnStatus,
                        onClick: handleConfirm,
                        disabled: disable,
                        hide: hide,
                      })}
                    </Box>
                  </Flex>
                ) : (
                  typeUserLogged === 'CLIENTE_ADMINISTRADOR' &&
                  (demand?.createdBy?.employeeDanielLaw === 'CLIENTE_ADMINISTRADOR' ||
                    demand?.createdBy?.employeeDanielLaw === 'CLIENTE_DANIEL_LAW') && (
                    <Flex justifyContent="space-between">
                      <Box>
                        <Button
                          leftIcon={{ name: 'pencil', marginRight: 'inherit' }}
                          onClick={() => setIsEditing(true)}
                          type="button"
                          variant="primary"
                          outline
                        >
                          Editar Demanda
                        </Button>
                      </Box>
                      <Box>
                        {ModalShow({
                          header: btnStatus,
                          onClick: handleConfirm,
                          disabled: disable,
                          hide: hide,
                        })}
                      </Box>
                    </Flex>
                  )
                )
              ) : (
                <Flex justifyContent="space-between">
                  <Box>
                    <Button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        history.goBack();
                      }}
                      outline
                    >
                      Cancelar
                    </Button>
                  </Box>
                  <Box>
                    <Button type="submit" isLoading={isSaving}>
                      Salvar
                    </Button>
                  </Box>
                </Flex>
              )}
            </Unform>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const NewDemand = memo(NewDemandComp);
